'use client'

import { Tooltip, TooltipContent, TooltipTrigger } from '@elmnts/ui/tooltip'
import { UserAvatar } from '@/components/UserAvatar'
import {
  ESTADO_NO_CANAL_LABEL,
  rotuloDoGrupo,
  type EstadoNoCanal,
  type PresencaNoCanal,
} from '@/lib/navigation/presencaNoCanal'
import { cn } from '@/lib/utils'

export type TamanhoDosAvatares = 'sm' | 'xs'

interface ChannelPresenceAvatarsProps {
  /** Já filtrada e ordenada (`montarPresencaNoCanal`). */
  lista: readonly PresencaNoCanal[]
  size?: TamanhoDosAvatares
  /** Só muda o nome do grupo pra AT ("no canal" | "no chat"). */
  onde?: 'canal' | 'chat'
  className?: string
}

const VISIVEIS = 3

/** Outline 2 px por estado. `box-shadow` e NUNCA `border-*`: o `UserAvatar` pinta
 *  `borderColor` inline quando o user tem cor. Classes LITERAIS — o Tailwind só
 *  gera o que encontra no fonte. */
const OUTLINE: Record<EstadoNoCanal, string> = {
  ausente: 'shadow-[0_0_0_2px_var(--color-warning)]',
  em_chat: 'shadow-[0_0_0_2px_#12d8f6]',
  online: 'shadow-[0_0_0_2px_var(--color-success)]',
}

const TAMANHO: Record<TamanhoDosAvatares, { caixa: string; avatar: string; texto: string; sobrepor: string }> = {
  // `sobrepor` ~40% da caixa: com o contorno de 2 px, menos que isso os avatares só encostam.
  sm: { caixa: 'size-6 rounded-md', avatar: 'size-6 rounded-md text-[9px]', texto: 'text-[10px]', sobrepor: '-ml-2.5' },
  xs: { caixa: 'size-5 rounded', avatar: 'size-5 rounded text-[8px]', texto: 'text-[9px]', sobrepor: '-ml-2' },
}

function legenda(p: PresencaNoCanal): string {
  return `${p.user.name} · ${ESTADO_NO_CANAL_LABEL[p.estado]}`
}

/**
 * Quem está no canal ou chat agora: até 3 avatares sobrepostos + `+N`, outline por estado.
 * Não-interativo (só `<span>`): cabe dentro do `<button>` do card e da linha do
 * BrandMenu. Cor nunca é a única pista — tooltip por avatar e `aria-label` do grupo.
 */
export function ChannelPresenceAvatars({ lista, size = 'sm', onde = 'canal', className }: ChannelPresenceAvatarsProps) {
  if (lista.length === 0) return null
  const visiveis = lista.slice(0, VISIVEIS)
  const resto = lista.slice(VISIVEIS)
  const t = TAMANHO[size]

  return (
    <span role="group" aria-label={rotuloDoGrupo(lista, onde)} className={cn('flex shrink-0 items-center', className)}>
      {/* O nome do grupo já diz tudo pra AT; os itens são visuais (tooltip pra mouse). */}
      <span aria-hidden="true" className="flex items-center">
        {visiveis.map((p, i) => {
          // Em chat, o próprio avatar já identifica quem está vendo a conversa; mantém amarelo de ausente.
          const outline = onde === 'chat' && p.estado === 'em_chat' ? null : OUTLINE[p.estado]
          return (
            <Tooltip key={p.user.db_id}>
              <TooltipTrigger asChild>
                <span
                  data-estado={p.estado}
                  // Primeiro (o mais relevante na ordem) por cima; o +N fica por baixo de todos.
                  style={{ zIndex: VISIVEIS + 1 - i }}
                  className={cn('relative inline-flex bg-background', t.caixa, outline, i > 0 && t.sobrepor)}
                >
                  <UserAvatar
                    size="sm"
                    name={p.user.name}
                    icon={p.user.avatar?.icon}
                    color={p.user.avatar?.color}
                    className={t.avatar}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent>{legenda(p)}</TooltipContent>
            </Tooltip>
          )
        })}
        {resto.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-slot="presenca-mais"
                style={{ zIndex: 1 }}
                className={cn(
                  'relative inline-flex items-center justify-center bg-muted font-semibold tabular-nums text-muted-foreground',
                  t.caixa,
                  t.texto,
                  t.sobrepor,
                )}
              >
                {`+${resto.length}`}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <ul className="flex flex-col gap-0.5">
                {resto.map((p) => (
                  <li key={p.user.db_id}>{legenda(p)}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}
      </span>
    </span>
  )
}
